import React, { useEffect, useState } from 'react';
import { 
  Box, Container, Typography, CircularProgress, Paper, Avatar, 
  Stepper, Step, StepLabel, IconButton, Button, Fade, Stack, 
  Link, TextField, InputAdornment, StepButton, useMediaQuery, useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';

import { useAgendamentoStore } from '../../store/useAgendamentoStore';
import { AgendamentoStep } from '../../agendamento.types';
import { AgendamentoService } from '../../services/agendamentoservice';
import { TEMAS_APP, ThemeType } from '../../temas/temas-config-ts';

// Steps
import { StepServico } from '../steps/StepServico';
import { StepHorario } from '../steps/StepHorario';
import { StepProfissional } from '../steps/StepProfissional';
import { StepResumo } from '../steps/StepResumo';
import { StepConsulta } from '../steps/StepConsultaAgendamentos';

const stepsLabel = ['Serviços', 'Horários', 'Profissional', 'Resumo'];

const Agendamento: React.FC = () => {
  // Pegamos as funções de set individuais para o "reset parcial"
  const { 
    step, unidade, servico, setUnidade, setStep, 
    setServico, setHorario, setProfissional 
  } = useAgendamentoStore();

  const [loading, setLoading] = useState(true);
  const [isConsultando, setIsConsultando] = useState(false);
  const [inputXS, setInputXS] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* =====================================================
      REINICIAR FLUXO (Mantendo a Unidade)
  ===================================================== */
  const handleNovoAgendamento = () => {
    // Limpamos apenas as seleções do agendamento atual
    setServico(undefined as any);
    setHorario(undefined as any, ''); 
    setProfissional(undefined as any);
    setStep(AgendamentoStep.SERVICOS);
    setIsConsultando(false);
  };

  useEffect(() => {
    const temaChave = (unidade?.tema as ThemeType) || 'DARK_MINIMAL';
    const tema = TEMAS_APP[temaChave] || TEMAS_APP.DARK_MINIMAL;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', tema.primary);
    root.style.setProperty('--color-background', tema.background);
    root.style.setProperty('--color-surface', tema.surface);
    root.style.setProperty('--color-text', tema.text);
  }, [unidade]);

  const carregarUnidadePorChave = async (chave: string) => {
    setLoading(true);
    try {
      sessionStorage.setItem("X-S", chave);
      const data = await AgendamentoService.listarUnidades(chave);
      if (data && data.length > 0) setUnidade(data[0]);
      else alert("Empresa não encontrada.");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let chave = "";
    urlParams.forEach((_, key) => { chave = key; });
    if (chave) carregarUnidadePorChave(chave);
    else {
      // Tenta recuperar do sessionStorage se não houver na URL
      const savedChave = sessionStorage.getItem("X-S");
      if (savedChave) carregarUnidadePorChave(savedChave);
      else setLoading(false);
    }
  }, [setUnidade]);

  const handleStepJump = (index: number) => {
    if (index + 1 < step) setStep(index + 1);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}>
      <CircularProgress sx={{ color: 'var(--color-primary)' }} />
    </Box>
  );

  if (!unidade && !isConsultando) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#09090b', display: 'flex', alignItems: 'center', px: 2 }}>
      <Container maxWidth="xs">
        <Fade in timeout={800}>
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 6, bgcolor: '#18181b', textAlign: 'center', border: '1px solid rgba(255,255,255,0.05)' }}>
            <BusinessIcon sx={{ fontSize: 40, color: '#fff', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" fontWeight={900} color="#fff" gutterBottom>Bem-vindo</Typography>
            <TextField 
              fullWidth placeholder="Nome da empresa..." value={inputXS}
              onChange={(e) => setInputXS(e.target.value.toLowerCase().trim())}
              onKeyDown={(e) => e.key === 'Enter' && carregarUnidadePorChave(inputXS)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 3 } }}
            />
            <Button fullWidth variant="contained" onClick={() => carregarUnidadePorChave(inputXS)} sx={{ bgcolor: '#fff', color: '#000', fontWeight: 900, py: 1.5, borderRadius: 3 }}>Acessar</Button>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--color-background)', py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 } }}>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            size="small"
            startIcon={isConsultando ? <EventIcon /> : <SearchIcon />} 
            onClick={() => setIsConsultando(!isConsultando)}
            sx={{ fontWeight: 800, color: 'var(--color-primary)', textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {isConsultando ? "Voltar ao Agendamento" : "Meus Agendamentos"}
          </Button>
        </Box>

        {!isConsultando && unidade && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: { xs: 4, sm: 6 }, textAlign: 'center', mb: 3, bgcolor: 'var(--color-surface)', border: '1px solid rgba(255,255,255,0.05)', position: 'relative' }}>
            {step > AgendamentoStep.SERVICOS && step <= AgendamentoStep.RESUMO && (
              <IconButton onClick={() => setStep(step - 1)} sx={{ position: 'absolute', left: 8, top: 8 }}>
                <ArrowBackIcon sx={{ color: 'var(--color-text)', fontSize: 20 }} />
              </IconButton>
            )}
            {unidade.logomarca ? (
              <Box component="img" src={unidade.logomarca.includes('base64') ? unidade.logomarca : `data:image/png;base64,${unidade.logomarca}`} sx={{ height: { xs: 40, sm: 55 }, mb: 1.5, mx: 'auto', display: 'block', objectFit: 'contain' }} />
            ) : (
              <Avatar sx={{ bgcolor: 'var(--color-primary)', width: 48, height: 48, mx: 'auto', mb: 1, color: '#000', fontWeight: 800 }}>{unidade.nome.charAt(0)}</Avatar>
            )}
            <Typography variant={isMobile ? "subtitle2" : "h5"} fontWeight={900} color="var(--color-text)" noWrap>{unidade.nome}</Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ opacity: 0.6, mt: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.4, fontSize: '0.65rem' }}><LocationOnIcon sx={{ fontSize: 10 }} /> {isMobile ? 'Localização' : unidade.enderecoCompleto}</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.4, fontWeight: 700, fontSize: '0.65rem' }}><WhatsAppIcon sx={{ fontSize: 10, color: '#25D366' }} /> {unidade.telefoneFormatado}</Typography>
            </Stack>
          </Paper>
        )}

        {!isConsultando && step <= AgendamentoStep.RESUMO && (
          <Box sx={{ mb: 3, px: 1 }}>
            <Stepper activeStep={step - 1} alternativeLabel nonLinear>
              {stepsLabel.map((label, index) => (
                <Step key={label}>
                  <StepButton onClick={() => handleStepJump(index)} sx={{ p: 0 }}>
                    <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.65rem', fontWeight: 700 } }}>
                      {!isMobile && label}
                    </StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        <Box sx={{ mt: 1 }}>
          {isConsultando ? (
            <StepConsulta />
          ) : (
            <Fade in timeout={400}>
              <Box>
                {step === AgendamentoStep.SERVICOS && <StepServico codigoUnidade={unidade!.codigo} />}
                {step === AgendamentoStep.HORARIOS && servico && <StepHorario servicoId={servico.codigo} unidadeId={unidade!.codigo} />}
                {step === AgendamentoStep.PROFISSIONAIS && <StepProfissional />}
                {step === AgendamentoStep.RESUMO && <StepResumo />}
                
                {step === AgendamentoStep.CONFIRMACAO && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', borderRadius: 6, bgcolor: 'var(--color-surface)', border: '1px solid rgba(255,255,255,0.05)', maxWidth: 420, width: '100%' }}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.03)', width: 70, height: 70, mx: 'auto', mb: 2, border: '2px solid var(--color-primary)' }}>
                        <EventIcon sx={{ fontSize: 35, color: 'var(--color-primary)' }} />
                      </Avatar>
                      <Typography variant="h5" fontWeight={900} color="var(--color-text)" gutterBottom>Agendado!</Typography>
                      <Typography variant="body2" color="var(--color-text)" sx={{ opacity: 0.6, mb: 4, px: 2, lineHeight: 1.6 }}>
                        Seu horário foi reservado com sucesso e já aparece na agenda da empresa.
                      </Typography>
                      
                      <Stack spacing={1.5}>
                      <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={handleNovoAgendamento} 
                        sx={{ bgcolor: 'var(--color-primary)', color: 'var(--color-background)', py: 1.5, borderRadius: 3, fontWeight: 900, mb: 2 }}
                      >
                        Novo Agendamento
                      </Button>
                        <Button 
                          variant="text" 
                          fullWidth 
                          onClick={() => setIsConsultando(true)} 
                          sx={{ color: 'var(--color-text)', opacity: 0.8, fontWeight: 700, fontSize: '0.8rem' }}
                        >
                          Ver meus horários
                        </Button>
                      </Stack>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        <Box sx={{ mt: 4, pb: 2, textAlign: 'center' }}>
          <Link href="https://gestao.appcodesh.com.br" target="_blank" sx={{ opacity: 0.2, fontSize: '9px', color: 'var(--color-text)', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 12 }} /> ACESSO ADMINISTRATIVO
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Agendamento;